'use strict';

/**
 * Single source of truth for employee-profile derived pay metrics.
 * Underpaid % = (market - pay) / market * 100  (market is denominator)
 *
 * Unverified employee.marketSalary is never used — only Adzuna-verified benchmarks.
 */

const { isValidBenchmark } = require('./marketValidation');
const {
  finiteNumber,
  roundPct,
  roundMoney,
  computeCompGapAmount,
  computeCompGapPct,
} = require('./compensationMetrics');
const {
  validateAndReconcilePayGapBundle,
  detectCompensationAnomalies,
} = require('./compensationMetricValidation');

function underpaidPercent(market, pay) {
  return computeCompGapPct(market, pay);
}

function totalCompAnnualFromBenchmark(employee, benchmark) {
  const tc = finiteNumber(benchmark?.totalComp?.annualTotal);
  if (tc != null) return tc;
  const base = finiteNumber(employee?.salary);
  return base;
}

function pickCanonical(employee, compBench) {
  const hasVerifiedBenchmark =
    isValidBenchmark(employee?.marketComparison) || isValidBenchmark(compBench);
  if (!hasVerifiedBenchmark) return null;

  if (compBench?.canonicalMarket && typeof compBench.canonicalMarket === 'object') {
    return compBench.canonicalMarket;
  }
  if (employee?.marketInsightsSnapshot && typeof employee.marketInsightsSnapshot === 'object') {
    return employee.marketInsightsSnapshot;
  }
  return null;
}

function benchmarkMetaFromComparison(marketComparison) {
  if (!marketComparison || typeof marketComparison !== 'object') return null;
  return {
    source: marketComparison.source || null,
    fetchedAt: marketComparison.fetchedAt || null,
    adzunaSnapshotRef: marketComparison.meta?.adzunaSnapshotRef || null,
    marketCacheKey: marketComparison.meta?.marketCacheKey || null,
  };
}

function finalizePayMetrics(raw, employee, source) {
  const reconciled = validateAndReconcilePayGapBundle({
    salaryAnnual: raw.salaryAnnual,
    benchmarkAnnual: raw.benchmarkAnnual,
    gapAmount: raw.gapAmount,
    gapPct: raw.gapPct,
    totalCompAnnual: raw.totalCompAnnual,
    benchmarkTotalAnnual: raw.benchmarkTotalAnnual,
    totalCompGapAmount: raw.totalCompGapAmount,
    totalCompGapPct: raw.totalCompGapPct,
    source,
    benchmarkMeta: raw.benchmarkMeta,
  });

  const metrics = reconciled.metrics;
  const pay = {
    available: true,
    payGapDollars: metrics.payGapDollars ?? 0,
    payGapPct: metrics.payGapPct,
    gapAmount: metrics.gapAmount,
    gapPct: metrics.gapPct,
    totalCompGapAmount: metrics.totalCompGapAmount,
    totalCompGapPct: metrics.totalCompGapPct,
    salaryAnnual: raw.salaryAnnual,
    benchmarkAnnual: raw.benchmarkAnnual,
    totalCompAnnual: raw.totalCompAnnual,
    benchmarkTotalAnnual: raw.benchmarkTotalAnnual,
    usesTotalComp: metrics.usesTotalComp,
    source,
    validation: reconciled.validation,
    anomalies: detectCompensationAnomalies(employee, {
      available: true,
      ...metrics,
      salaryAnnual: raw.salaryAnnual,
      benchmarkAnnual: raw.benchmarkAnnual,
      totalCompAnnual: raw.totalCompAnnual,
      benchmarkTotalAnnual: raw.benchmarkTotalAnnual,
    }),
  };

  if (reconciled.anomalies.length) {
    pay.anomalies = [...(pay.anomalies || []), ...reconciled.anomalies];
  }

  return pay;
}

/**
 * @param {object|null} employee
 * @param {object|null} compBench
 * @returns {{
 *   available: boolean,
 *   payGapDollars: number,
 *   payGapPct: number|null,
 *   gapAmount: number|null,
 *   gapPct: number|null,
 *   totalCompGapAmount: number|null,
 *   totalCompGapPct: number|null,
 *   salaryAnnual: number|null,
 *   benchmarkAnnual: number|null,
 *   totalCompAnnual: number|null,
 *   benchmarkTotalAnnual: number|null,
 *   usesTotalComp: boolean,
 *   source: string|null,
 *   validation?: object,
 *   anomalies?: object[],
 * }}
 */
function computePayGapMetrics(employee, compBench = null) {
  const empty = {
    available: false,
    payGapDollars: 0,
    payGapPct: null,
    gapAmount: null,
    gapPct: null,
    totalCompGapAmount: null,
    totalCompGapPct: null,
    salaryAnnual: finiteNumber(employee?.salary),
    benchmarkAnnual: finiteNumber(employee?.marketSalary),
    totalCompAnnual: totalCompAnnualFromBenchmark(employee, compBench),
    benchmarkTotalAnnual: null,
    usesTotalComp: false,
    source: null,
  };

  const canonical = pickCanonical(employee, compBench);
  if (canonical) {
    const benchmarkMeta = benchmarkMetaFromComparison(
      compBench?.status === 'available' ? compBench : employee?.marketComparison
    );
    return finalizePayMetrics(
      {
        gapAmount: roundMoney(canonical.gapAmount),
        gapPct: roundPct(canonical.gapPct),
        totalCompGapAmount: roundMoney(canonical.totalCompGapAmount),
        totalCompGapPct: roundPct(canonical.totalCompGapPct),
        benchmarkTotalAnnual: finiteNumber(canonical.benchmarkTotalAnnual),
        totalCompAnnual: finiteNumber(canonical.totalCompAnnual),
        salaryAnnual: finiteNumber(canonical.salaryAnnual) ?? empty.salaryAnnual,
        benchmarkAnnual: finiteNumber(canonical.benchmarkAnnual) ?? empty.benchmarkAnnual,
        benchmarkMeta,
      },
      employee,
      canonical.source || 'canonical'
    );
  }

  if (compBench?.status === 'available' && isValidBenchmark(compBench)) {
    const snapshot = compBench.marketSnapshot || {};
    const medianTotal = finiteNumber(snapshot?.medianCompensation?.totalAnnual);
    const medianBase = finiteNumber(snapshot?.medianCompensation?.baseAnnual);
    const totalCompAnnual = totalCompAnnualFromBenchmark(employee, compBench);
    const salaryAnnual = finiteNumber(employee?.salary);

    if (medianTotal != null && totalCompAnnual != null) {
      return finalizePayMetrics(
        {
          gapAmount:
            medianBase != null && salaryAnnual != null
              ? computeCompGapAmount(medianBase, salaryAnnual)
              : null,
          gapPct:
            medianBase != null && salaryAnnual != null
              ? computeCompGapPct(medianBase, salaryAnnual)
              : null,
          totalCompGapAmount: computeCompGapAmount(medianTotal, totalCompAnnual),
          totalCompGapPct: computeCompGapPct(medianTotal, totalCompAnnual),
          salaryAnnual,
          benchmarkAnnual: medianBase,
          totalCompAnnual,
          benchmarkTotalAnnual: medianTotal,
          benchmarkMeta: benchmarkMetaFromComparison(compBench),
        },
        employee,
        'compBench.marketSnapshot'
      );
    }
  }

  const storedBenchmark = employee?.marketComparison;
  if (isValidBenchmark(storedBenchmark)) {
    const snapshot = storedBenchmark.marketSnapshot || {};
    const medianTotal = finiteNumber(snapshot?.medianCompensation?.totalAnnual);
    const medianBase = finiteNumber(snapshot?.medianCompensation?.baseAnnual);
    const salaryAnnual = finiteNumber(employee?.salary);
    const totalCompAnnual = totalCompAnnualFromBenchmark(employee, {
      totalComp: storedBenchmark.totalComp,
    });

    if (medianTotal != null && totalCompAnnual != null) {
      return finalizePayMetrics(
        {
          gapAmount:
            medianBase != null && salaryAnnual != null
              ? computeCompGapAmount(medianBase, salaryAnnual)
              : null,
          gapPct:
            medianBase != null && salaryAnnual != null
              ? computeCompGapPct(medianBase, salaryAnnual)
              : null,
          totalCompGapAmount: computeCompGapAmount(medianTotal, totalCompAnnual),
          totalCompGapPct: computeCompGapPct(medianTotal, totalCompAnnual),
          salaryAnnual,
          benchmarkAnnual: medianBase,
          totalCompAnnual,
          benchmarkTotalAnnual: medianTotal,
          benchmarkMeta: benchmarkMetaFromComparison(storedBenchmark),
        },
        employee,
        'employee.marketComparison'
      );
    }
  }

  return empty;
}

/**
 * Profile-wide metrics bundle for UI sections (pay, brief, insights).
 * @param {{ employee?: object|null, compBench?: object|null }} args
 */
function computeEmployeeProfileMetrics({ employee = null, compBench = null } = {}) {
  const pay = computePayGapMetrics(employee, compBench);
  return {
    pay,
    payGapDollars: pay.payGapDollars,
    payGapPct: pay.payGapPct,
    validation: pay.validation || null,
    anomalies: pay.anomalies || [],
  };
}

module.exports = {
  computePayGapMetrics,
  computeEmployeeProfileMetrics,
  underpaidPercent,
};
